import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

const WS_URL = import.meta.env.VITE_WS_URL || '';

interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: string;
}

interface Notification {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  read: boolean;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  notifications: Notification[];
  sendMessage: (type: string, data: any) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
}

export function useWebSocket(): UseWebSocketReturn {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    // Get auth token
    const token = localStorage.getItem('access_token');
    if (!token) {
      return;
    }

    // Initialize socket connection
    const socketInstance = io(WS_URL, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
    });

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Message handlers
    socketInstance.on('lead_detected', (data) => {
      console.log('New lead detected:', data);
      addNotification({
        type: 'lead_detected',
        message: `New ${data.classification} lead detected from ${data.source_platform}`,
        data,
      });
      toast.success('New lead detected!');
    });

    socketInstance.on('message_generated', (data) => {
      console.log('Message generated:', data);
      addNotification({
        type: 'message_generated',
        message: `New message awaiting approval for lead ${data.lead_id}`,
        data,
      });
      toast.info('New message awaiting approval');
    });

    socketInstance.on('message_approved', (data) => {
      console.log('Message approved:', data);
      addNotification({
        type: 'message_approved',
        message: `Message approved and sent to ${data.platform}`,
        data,
      });
    });

    socketInstance.on('lead_responded', (data) => {
      console.log('Lead responded:', data);
      addNotification({
        type: 'lead_responded',
        message: `Lead ${data.lead_id} has responded to your message!`,
        data,
      });
      toast.success('Lead responded!', { duration: 5000 });
    });

    socketInstance.on('conversion_detected', (data) => {
      console.log('Conversion detected:', data);
      addNotification({
        type: 'conversion',
        message: `Lead ${data.lead_id} converted! Booking value: ${data.booking_value} ${data.currency}`,
        data,
      });
      toast.success('🎉 New conversion!', { duration: 6000 });
    });

    socketInstance.on('agent_error', (data) => {
      console.error('Agent error:', data);
      addNotification({
        type: 'error',
        message: `Error in ${data.agent_type}: ${data.error_message}`,
        data,
      });
      toast.error(`Agent error: ${data.error_message}`);
    });

    socketInstance.on('system_alert', (data) => {
      console.warn('System alert:', data);
      addNotification({
        type: 'alert',
        message: data.message,
        data,
      });
      toast(data.message, {
        icon: '⚠️',
        duration: 5000,
      });
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, []);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'read' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      read: false,
    };

    setNotifications((prev) => [newNotification, ...prev].slice(0, 50)); // Keep last 50 notifications
  }, []);

  const sendMessage = useCallback(
    (type: string, data: any) => {
      if (socket && isConnected) {
        socket.emit(type, data);
      } else {
        console.warn('Cannot send message: Socket not connected');
      }
    },
    [socket, isConnected]
  );

  const markNotificationRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    isConnected,
    notifications,
    sendMessage,
    markNotificationRead,
    clearNotifications,
  };
}

export default useWebSocket;